import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkPickupScheduledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to mark a vehicle pickup as scheduled
 */
export const markPickupScheduled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<MarkPickupScheduledResponse> => {
	const response = await apiClient.post<MarkPickupScheduledResponse>(
		ordersEndpoints.markPickupScheduled(servicesProviderId, orderId),
		{},
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) pickup as scheduled
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkPickupScheduled = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark pickup as scheduled without an order ID or services provider ID.',
				);
			}

			return markPickupScheduled(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_PICKUP_SCHEDULED);
		},
	});
};
